/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnDestroy, type OnInit, ChangeDetectorRef, Component, Injector, Input, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
    type ButtonGroup, type CardConfig,
    type CardTitleConfig, type Command, type HttpResponse, type OptionsParams, type OptionsResult,
    ButtonService, DataCardComponent, ERROR_OK, EventService,
    FormControl, HttpService, LayoutComponent, LazyloadService, SelectComponent, TimeService, ToastService,
    UNPLACEHOLDER, UtilService,
} from '@dm/core';
import { blockSizeE, FileSystemModeE, SYSTEM_VSTORE_ID, SYSTEM_VSTORE_NAME } from 'app/common/constants';
import { EventServiceKeyE } from 'app/common/constants/event-service-key';
import { ScheduleTypeE } from 'app/common/constants/hyper-cdp.const';
import { LicenseKey } from 'app/common/constants/license-key';
import { RemoteExecuteService, SmartGuiService, TaskService, ToolService } from 'app/common/services';
import { type CIFSClient, type CIFSShare, type FileSystem, type FileSystemResource, type HyperCdpSchedule, type NFSClient, type NFSShare } from 'app/common/types/mo';
import { ProductConfig, SWITCH } from 'app/config/switch';
import { type MessageTipModle } from 'app/feature/auth/auth';
import { DEFAULT_CDP_SCHEDULE_NAME } from 'app/feature/protection/schedule/hyper-cdp/hyper-cdp.const';
import { CreateComponent } from 'app/feature/protection/schedule/hyper-cdp/schedule/create/create.component';
import { objectTypeE } from 'app/feature/protection/schedule/hyper-cdp/schedule/create/create-str.service';
import { ScheduleService } from 'app/feature/protection/schedule/hyper-cdp/schedule/schedule.service';
import { SIMPLE_CDP_SCHEDULE_NAS_DEFAULT } from 'app/feature/protection/simple-protection/simple-protection.const';
import { SmartCacheService } from 'app/feature/service/resource-tuning/smart-cache/smart-cache.service';
import { StoragePoolService } from 'app/feature/system/storage-pool/storage-pool.service';
import { StoragePoolStrService } from 'app/feature/system/storage-pool/storage-pool-str.service';
import { type Observable, type Subscription, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { UserQuotaService } from '../../quota/user-quota/user-quota.service';
import { UserQuotaStrService } from '../../quota/user-quota/user-quota-str.service';
import { CopyFromCifsShareSComponent } from '../../share/cifs-share/copy-from-cifs-share-s/copy-from-cifs-share-s.component';
import { CopyFromNfsShareSComponent } from '../../share/nfs-share/copy-from-nfs-share-s/copy-from-nfs-share-s.component';
import { GlobalNamespaceE, OffsetE } from '../../share/nfs-share/nfs-share.enum';
import { NfsShareService } from '../../share/nfs-share/nfs-share.service';
import { NfsShareStrService } from '../../share/nfs-share/nfs-share-str.service';
import { CreateStyleE } from '../../share/share.enum';
import { CreateFileSystemComponent, FileSystemEntryE } from '../create-file-system/create-file-system.component';
import { type SimpleFileShareModifyShareNameParams } from '../file-system';
import { FileSystemService } from '../file-system.service';
import { type ShareResultParams, CreateFileShareTypeE, FileSystemStrService, FileTaskNameE, SecurityModeE, ShareEntryE } from '../file-system-str.service';
import { ModifyShareNameComponent } from './modify-share-name/modify-share-name.component';

@Component({
    selector: 'app-create-file-share',
    templateUrl: './create-file-share.component.html',
    styleUrls: ['../create-file-system/create-file-system.component.scss', './create-file-share.component.scss'],
    providers: [
        StoragePoolService,
        StoragePoolStrService,
        LayoutComponent,
    ],
})
export class CreateFileShareComponent extends CreateFileSystemComponent implements OnInit, OnDestroy {
    @ViewChild('copyFromNfsShareS') copyFromNfsShareS: CopyFromNfsShareSComponent;
    @ViewChild('copyFromCifsShareS') copyFromCifsShareS: CopyFromCifsShareSComponent;
    @ViewChild('nDataCard') nDataCard: DataCardComponent;
    @ViewChild('fsSelect') fsSelect: SelectComponent;

    @Input() fileSystem: FileSystem = {};
    /**
     * 由哪个入口进入创建文件共享
     */
    @Input() shareEntry: ShareEntryE;

    dataCardConfig: CardConfig;

    // 无存储池场景提示内容
    noPoolMessageTip: MessageTipModle;

    // 创建文件共享方式
    CreateFileShareTypeE = CreateFileShareTypeE;
    ShareEntryE = ShareEntryE;
    ProductConfig = ProductConfig;

    /**
     * NFS&CIFS权限列表入口控制【正常FS场景、审计日志FS场景】
     */
    copyFromShareEntry: FileSystemEntryE;

    // 创建文件共享方式宽度
    multiTitleWidth = '270px';

    // 高级设置默认值
    advanceUnfoldExpand = false;

    // 是否允许更改文件系统
    canFileSystemChange = true;

    scheduleDefaultData = [];

    cdpScheduleRest = this.scheduleService.getBatchRest(ScheduleTypeE.File, SYSTEM_VSTORE_ID);

    selectedFilesystem: FileSystem;
    fileSystemCreateShareNewStr = '';
    fileSystemCreateShareExistStr = '';
    hideCifsShare = false;

    shareResult: ShareResultParams;

    shareData: SimpleFileShareModifyShareNameParams = {
        nfsShareName: '',
        cifsShareName: '',
    };

    fsOptionsFactory: (params: OptionsParams) => Observable<OptionsResult>;

    isRootDirectory = false;

    UNPLACEHOLDER = UNPLACEHOLDER;

    GlobalNamespaceE = GlobalNamespaceE;

    blockSizeE = blockSizeE;

    // 页面要展示的share_path
    get share_path(): string {
        return this._share_path;
    }
    set share_path(value: string) {
        this._share_path = value;
    }
    _share_path = '';

    scheduleBtnGroup: ButtonGroup;

    /**
     * 监听智能助手是否点击“会弹出侧滑框”的按钮
     */
    watchAssistant$: Subscription;

    constructor(
        private router: Router,
        injector: Injector,
        public str: FileSystemStrService,
        public service: FileSystemService,
        public toolService: ToolService,
        public utilService: UtilService,
        storagePoolService: StoragePoolService,
        smartguiService: SmartGuiService,
        taskService: TaskService,
        remoteExecuteService: RemoteExecuteService,
        http: HttpService,
        smartCacheServcie: SmartCacheService,
        scheduleService: ScheduleService,
        quotaService: UserQuotaService,
        quotaSrt: UserQuotaStrService,
        timeService: TimeService,
        private NFSService: NfsShareService,
        public changeDetectorRef: ChangeDetectorRef,
        private buttonService: ButtonService,
        private lazyLoad: LazyloadService,
        private eventService: EventService,
        private toastService: ToastService,
        public nfsShareStrService: NfsShareStrService,
    ) {
        super(
            injector,
            str,
            service,
            toolService,
            utilService,
            storagePoolService,
            smartguiService,
            taskService,
            remoteExecuteService,
            http,
            smartCacheServcie,
            scheduleService,
            quotaService,
            quotaSrt,
            timeService,
        );

        this.noPoolMessageTip = {
            type: 'error',
            contentHandler: [
                {
                    text: this.i18n.noPoolMessageTip1,
                },
                {
                    text: this.i18n.noPoolMessageTip2,
                    handler: (): void => {
                        // 跳转存储池界面打开创建界面
                        this.router.navigate(['/system/storage-pool'], {
                            queryParams: {
                                action: 'create',
                            },
                        });
                    },
                },
                {
                    text: this.i18n.noPoolMessageTip3,
                },
            ],
        };
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.initBtn();

        this.dataCardConfig = {
            title: {
                field: 'NAME',
            },
            tip: {
                field: 'NAME',
                render: (tip: string, config: CardTitleConfig, data: HyperCdpSchedule): string => {
                    if (data && data.NAME === DEFAULT_CDP_SCHEDULE_NAME && !data.ID) {
                        return this.str.i18n.noDefaultCdpScheduleTip;
                    }
                    return this.scheduleService.renderStrategy(data, true).join('; ');
                },
            },
            rows: 6,
            pageStyle: 'default',
        };

        if (!this.fileSystem.NAME) {
            this.fsOptionsFactory = this.createFsOptionsFactory();
        }

        // 填入默认策略，没有策略的情况下需要DM下发创建
        this.scheduleDefaultData = [SIMPLE_CDP_SCHEDULE_NAS_DEFAULT] as unknown as HyperCdpSchedule[];

        if (SWITCH.IS_SIMPLE_MODE) {
            this.watchAssistant$ = this.eventService.subscribe(EventServiceKeyE.ClickAssistantCreateBtn, () => {
                this.hide();
            });
        }

        setTimeout(() => {
            if (this.selectedFilesystem) {
                this.updateFileSystemInfo(this.selectedFilesystem);
            }
        }, 500);
    }

    createFsOptionsFactory() {
        return ({ limit, offset, keyword }: OptionsParams): Observable<OptionsResult> => {
            const filters = [];

            // 是否添加根目录文件系统，首次请求文件系统数据或者搜索"/"时增加
            const isAddRootFs = (offset === OffsetE.NONE && keyword === '') || keyword === GlobalNamespaceE.NAME;

            if (keyword) {
                filters.push({
                    field: 'NAME',
                    value: keyword,
                    matchMode: ':',
                });
            }

            const range = `[${offset}-${offset + limit}]`;
            const params = {
                vstoreId: SYSTEM_VSTORE_ID,
                range,
            };

            return forkJoin({
                count: this.NFSService.getBatchCountFs(filters, { vstoreId: SYSTEM_VSTORE_ID }),
                fileSystems: this.NFSService.getBatchFs(filters, params),
            }).pipe(
                map((res) => {
                    const fsOptions = res.fileSystems.data.map((fs: FileSystem) => ({
                        label: fs.NAME,
                        value: fs.ID,
                        data: fs,
                    }));

                    if (isAddRootFs) {
                        fsOptions.unshift({
                            label: GlobalNamespaceE.NAME,
                            value: GlobalNamespaceE.ID,
                            data: {
                                ID: GlobalNamespaceE.ID,
                                NAME: GlobalNamespaceE.NAME,
                                vstoreId: SYSTEM_VSTORE_ID,
                            },
                        });
                    }

                    const count = res.count.data.COUNT;

                    return {
                        finished: Number(count) <= (offset + limit),
                        options: fsOptions,
                    };
                }),
            );
        };
    }

    initVm(): void {
        super.initVm();

        this.vm.addControl('createFileShareType', new FormControl(
            this.fileSystem.NAME ? CreateFileShareTypeE.EXIST : CreateFileShareTypeE.NEW));
        this.vm.addControl('FS', new FormControl(this.selectedFilesystem, [
            Validators.required,
        ]));

        this.vm.patchValue({ securityStyle: SecurityModeE.Native });

        this.vm.get('FS').disable();

        this.vm.get('name').valueChanges.subscribe((newValue: string) => {
            const oldValue = this.vm.value.name;

            if (newValue === oldValue || this.vm.value.createFileShareType === CreateFileShareTypeE.EXIST) {
                return;
            }

            this.updateSharePathData(newValue);
        });

        if (this.fileSystem?.NAME) {
            this.share_path = `/${this.fileSystem.NAME}/`;
            this.canFileSystemChange = false;
            this.selectedFilesystem = this.fileSystem;

            // value 数组格式 [NFS数量，CIFS数量]
            const values = this.fileSystem.shareNumbers;

            // 构造成{ NFS: true }的形式
            this.shareResult = {
                NFS: values[0] > 0,
                CIFS: values[1] > 0,
            };

            this.createFileShareTypeChange(this.vm.value.createFileShareType);
            this.vm.get('FS').disable();

            this.updateHideCifsShareStatus();

            this.updateCopyFromShareEntry();
        }
    }

    /**
     * 初始化按钮
     */
    initBtn(): void {
        this.scheduleBtnGroup = [this.buttonService.create({
            id: 'hyper-cdp-schedule-main-create-button',
            theme: 'primary',
            label: this.i18n.addNewScheduleBtn,
            permission: ['@hyper_cdp_W'],
            license: [LicenseKey.HyperCDP],
            command: () => {
                this.lazyLoad.appendComponent(CreateComponent, {
                    type: ScheduleTypeE.File,
                    vstore: {
                        label: SYSTEM_VSTORE_NAME,
                        value: SYSTEM_VSTORE_ID,
                    },
                    onClose: (createObj: HyperCdpSchedule) => {
                        if (!createObj) {
                            return;
                        }

                        this.nDataCard?.autoSelectLastPageItemByRule({
                            key: 'ID',
                            value: createObj.ID,
                        });
                    },
                });
            },
        })];
    }

    updateHideCifsShareStatus(): void {
        this.hideCifsShare = this.vm.value.createFileShareType === CreateFileShareTypeE.EXIST &&
            this.selectedFilesystem && this.selectedFilesystem.ID === GlobalNamespaceE.ID;
    }

    updateSharePathData(fsName: string): void {
        if (!fsName) {
            return;
        }
        // 更新nfs共享名称
        this.copyFromNfsShareS?.changeSharePath(fsName);

        // 更新cifs共享名称
        this.copyFromCifsShareS?.changeSharePathAndName(fsName, false);

        this.fileSystemCreateShareNewStr = this.updateFileSystemCreateShareStr();
    }

    /**
     * 更新共享提示信息
     */
    updateFileSystemCreateShareStr(): string {
        let str: string;
        // 创建NFS共享、创建cifs共享
        if (this.copyFromNfsShareS?.switch && !this.shareResult?.NFS && this.copyFromCifsShareS?.switch) {
            str = this.i18n.fileSystemCreateShareInfo(this.copyFromNfsShareS.createShare[CreateStyleE.All].share?.NAME ?
                this.copyFromNfsShareS.createShare[CreateStyleE.All].share?.NAME : this.copyFromNfsShareS.showNfsShareName,
                this.copyFromCifsShareS.createShare[CreateStyleE.All].share.NAME,
                this.copyFromCifsShareS.createShare[CreateStyleE.All].share.SHAREPATH);
        }

        // 仅创建nfs共享
        if (this.copyFromNfsShareS?.switch && !this.shareResult?.NFS && !this.copyFromCifsShareS?.switch) {
            str = this.i18n.newShareNameNfsInfo(this.copyFromNfsShareS.createShare[CreateStyleE.All].share?.NAME ?
                this.copyFromNfsShareS.createShare[CreateStyleE.All].share?.NAME : this.copyFromNfsShareS.showNfsShareName,
                this.copyFromNfsShareS.createShare[CreateStyleE.All].share.SHAREPATH);
        }

        // 仅创建cifs共享
        if ((!this.copyFromNfsShareS?.switch || this.shareResult?.NFS) && this.copyFromCifsShareS?.switch) {
            str = this.i18n.newShareNameCifsInfo(this.copyFromCifsShareS.createShare[CreateStyleE.All].share.NAME,
                this.copyFromCifsShareS.createShare[CreateStyleE.All].share.SHAREPATH);
        }

        return str;
    }

    // 共享开关变更时，更新提示信息
    shareSwitchUpdate(): void {
        this.fileSystemCreateShareNewStr = this.updateFileSystemCreateShareStr();
        this.fileSystemCreateShareExistStr = this.updateFileSystemCreateShareStr();
    }

    nfsShareSwitchUpdate(): void {
        this.shareSwitchUpdate();

        this.nfsSharePermissionUpdate();
    }

    // 共享开关变更时，更新提示信息
    cifsShareSwitchUpdate(): void {
        this.shareSwitchUpdate();

        this.cifsSharePermissionUpdate();
    }

    getShareNameChangedData(value: string): void {
        if (!value) {
            return;
        }

        this.fileSystemCreateShareExistStr = this.updateFileSystemCreateShareStr();
        this.changeDetectorRef.detectChanges();
    }

    createFileShareTypeChange(value: CreateFileShareTypeE): void {
        if (value === CreateFileShareTypeE.NEW) {
            this.vm.get('name').enable();
            this.vm.get('capacity').enable();
            this.vm.get('selectedCdpSchedule').enable();
            this.vm.get('FS').disable();

            this.updateSharePathData(this.vm.value.name);

            // 更新【新创建FS场景共享提示信息数据&状态】
            this.updateFileSystemCreateShareExistStr();
        } else {
            this.vm.get('name').disable();
            this.vm.get('capacity').disable();
            this.vm.get('selectedCdpSchedule').disable();
            this.vm.get('FS').enable();

            // 更新【已有FS场景根据当前选择的FS数据更新共享提示信息&状态】
            this.updateFileSystemInfo(this.selectedFilesystem);
        }

        this.updateHideCifsShareStatus();
    }

    /**
     * 选择文件系统后，需要更新dtree和sharepath
     */
    onChooseFileSystem($event): void {
        if (!$event.value) {
            this.share_path = '';
            return;
        }
        this.share_path = $event.value !== GlobalNamespaceE.ID ? `/${$event.option.data.NAME}/` : $event.option.data.NAME;
        this.isRootDirectory = $event.value === GlobalNamespaceE.ID;

        this.selectedFilesystem = $event.option.data;

        this.updateFileSystemInfo(this.selectedFilesystem);

        this.updateHideCifsShareStatus();

        this.updateCopyFromShareEntry();
    }

    /**
     * 更新共享权限列表入口
     */
    updateCopyFromShareEntry(): void {
        // 内部文件系统和哈希文件系统限制共享权限
        if (this.selectedFilesystem.isAuditLogFs ||
            this.selectedFilesystem.SUBTYPE === FileSystemModeE.SMART_MOBILITY_INNER_FILE_SYSTEM ||
            this.selectedFilesystem.SUBTYPE === FileSystemModeE.HASH) {
            this.copyFromShareEntry = FileSystemEntryE.AuditLog;

            this.nfsSharePermissionUpdate();
            this.cifsSharePermissionUpdate();
        } else {
            this.copyFromShareEntry = FileSystemEntryE.Main;
        }
    }

    nfsSharePermissionUpdate(): void {
        if (this.auditLogFsClearNfsPermission()) {
            this.toastService.warning(this.i18n.clearNfsPermissionLab);
        }
    }

    cifsSharePermissionUpdate(): void {
        if (this.auditLogFsClearCifsPermission()) {
            this.toastService.warning(this.i18n.clearCifsPermissionLab);
        }
    }

    /**
     * 审计日志文件系统清空NFS共享权限
     * @returns
     */
    auditLogFsClearNfsPermission(): boolean {
        // 选择FS场景&选择的是审计日志文件系统场景才进行清空权限操作
        if (!(this.vm.value.createFileShareType === CreateFileShareTypeE.EXIST && this.selectedFilesystem?.isAuditLogFs) || this.fileSystem?.NAME) {
            return false;
        }

        // 是否需要清空共享权限提示标记
        let needShowClearPermissionTip = false;

        // 需要清空NFS共享权限场景【1.开启NFS开关; 2.选择的FS未创建NFS共享; 3.选择的指定共享权限】
        const needClearNfsPermission = this.copyFromNfsShareS?.switch &&
            !this.shareResult?.NFS &&
            this.copyFromNfsShareS.vm.value.NFScreateStyle === CreateStyleE.New;
        if (needClearNfsPermission && this.copyFromNfsShareS?.createShare[this.copyFromNfsShareS.vm.value.NFScreateStyle]?.permissions?.length) {
            needShowClearPermissionTip = true;
            this.copyFromNfsShareS.clientList = [];
            this.copyFromNfsShareS.createShare[this.copyFromNfsShareS.vm.value.NFScreateStyle].permissions = [];
        }

        return needShowClearPermissionTip;
    }

    /**
     * 审计日志文件系统清空CIFS共享权限
     * @returns
     */
    auditLogFsClearCifsPermission(): boolean {
        // 选择FS场景&选择的是审计日志文件系统场景才进行清空权限操作
        if (!(this.vm.value.createFileShareType === CreateFileShareTypeE.EXIST && this.selectedFilesystem?.isAuditLogFs) || this.fileSystem?.NAME) {
            return false;
        }

        // 是否需要清空共享权限提示标记
        let needShowClearPermissionTip = false;

        // 需要清空CIFS共享权限场景【1.开启CIFS开关; 2.选择的指定共享权限】
        const needClearCifsPermission = this.copyFromCifsShareS?.switch &&
            this.copyFromCifsShareS.vm.value.CIFScreateStyle === CreateStyleE.New;
        if (needClearCifsPermission && this.copyFromCifsShareS?.createShare[this.copyFromCifsShareS.vm.value.CIFScreateStyle]?.permissions?.length) {
            needShowClearPermissionTip = true;
            this.copyFromCifsShareS.userList = [];
            this.copyFromCifsShareS.createShare[this.copyFromCifsShareS.vm.value.CIFScreateStyle].permissions = [];
        }

        return needShowClearPermissionTip;
    }

    updateFileSystemInfo(fs: FileSystem): void {
        const cifsInValid = (!ProductConfig.isHideCifs && !this.copyFromCifsShareS);
        if (!fs || !this.copyFromNfsShareS || cifsInValid) {
            return;
        }

        // 根文件系统
        if (this.selectedFilesystem && this.selectedFilesystem.ID === GlobalNamespaceE.ID) {
            this.fileSystemCreateShareExistStr =
                this.i18n.fileSystemCreateShareRootInfo(GlobalNamespaceE.NAME, GlobalNamespaceE.NAME);

            this.copyFromNfsShareS.switch = true;
            this.copyFromNfsShareS.isExistNfs = false;
            if (this.shareResult) {
                this.shareResult.NFS = false;
            }
            this.copyFromNfsShareS.onSwitchChange(this.copyFromNfsShareS.switch);

            this.fileSystemCreateShareExistStr =
                this.i18n.fileSystemCreateShareRootInfo(GlobalNamespaceE.NAME, GlobalNamespaceE.NAME);
            return;
        }

        // 更新nfs共享名称
        this.copyFromNfsShareS?.changeSharePath(fs.NAME);

        // value 数组格式 [NFS数量，CIFS数量]
        const values = fs.shareNumbers;

        // 构造成{ NFS: true }的形式
        this.shareResult = {
            NFS: values[0] > 0,
            CIFS: values[1] > 0,
        };

        // 更新cifs共享名称
        this.copyFromCifsShareS?.changeSharePathAndName(fs.NAME, this.shareResult?.CIFS);

        this.updateFileSystemCreateShareExistStr();
    }

    updateFileSystemCreateShareExistStr(): void {
        if (this.shareResult?.NFS && this.vm.value.createFileShareType === CreateFileShareTypeE.EXIST) {
            // 选择已有文件系统场景，FS已存在NFS共享，默认打开CIFS开关：避免高级收缩起来且未配置共享点不了确定
            this.copyFromCifsShareS.switch = true;
            this.copyFromCifsShareS.onSwitchChange(this.copyFromCifsShareS.switch);
            this.copyFromNfsShareS.isExistNfs = true;
            this.copyFromNfsShareS.onSwitchChange(this.copyFromNfsShareS.switch);
        } else {
            this.copyFromNfsShareS.isExistNfs = false;
            if (this.shareResult) {
                this.shareResult.NFS = false;
            }
            this.copyFromNfsShareS.onSwitchChange(this.copyFromNfsShareS.switch);
        }

        this.fileSystemCreateShareExistStr = this.updateFileSystemCreateShareStr();
    }

    /**
     *
     * @returns 修改共享名称
     */
    modifyShareName(): void {
        this.shareData.showNfsShareForm = this.copyFromNfsShareS?.switch && !this.shareResult?.NFS;
        this.shareData.nfsShareName = this.copyFromNfsShareS.createShare[CreateStyleE.All].share?.NAME ?
            this.copyFromNfsShareS.createShare[CreateStyleE.All].share?.NAME : '';
        this.shareData.cifsShareName = !this.copyFromCifsShareS?.switch ?
            undefined : this.copyFromCifsShareS.createShare[CreateStyleE.All].share.NAME;

        this.lazyLoad.appendComponent(
            ModifyShareNameComponent,
            {
                data: this.shareData,
                update: (data: SimpleFileShareModifyShareNameParams) => {
                    Object.assign(this.shareData, data);
                    if (this.shareData?.nfsShareName) {
                        this.copyFromNfsShareS?.updateNfsShareName(this.shareData.nfsShareName);
                    } else {
                        // 修改共享名称界面支持NFS并且数据为空才删除NFS共享名称源数据
                        if (this.shareData?.showNfsShareForm) {
                            delete this.copyFromNfsShareS.createShare[CreateStyleE.All].share?.NAME;
                        }
                    }
                    if (this.shareData?.cifsShareName) {
                        this.copyFromCifsShareS?.updateCifsShareName(this.shareData.cifsShareName);
                    }
                    this.fileSystemCreateShareExistStr = this.updateFileSystemCreateShareStr();
                },
            },
        );
    }

    buildCreateFileSystemCmd(): Command[] {
        if (this.vm.value.createFileShareType === CreateFileShareTypeE.NEW) {
            return super.buildCreateFileSystemCmd();
        } else {
            const cmds: Command[] = [];

            const vmValue = this.vm.value;

            // 根文件系统与NFS关闭场景 不下发命令
            const globalNamespaceValid = !vmValue.NFScreateShare && this.selectedFilesystem && this.selectedFilesystem.ID === GlobalNamespaceE.ID;
            if ((!vmValue.NFScreateShare && !vmValue.CIFScreateShare) || globalNamespaceValid) {
                return cmds;
            }

            // 根文件系统创建NFS共享场景不走omtask
            if (vmValue.NFScreateShare &&
                this.vm.value.createFileShareType === CreateFileShareTypeE.EXIST &&
                this.selectedFilesystem &&
                this.selectedFilesystem.ID === GlobalNamespaceE.ID) {
                const params: NFSShare = {
                    SHAREPATH: GlobalNamespaceE.NAME,
                    vstoreId: SYSTEM_VSTORE_ID,
                };

                cmds.push({
                    id: 'file_share_nfs_share',
                    name: this.i18n.createNfsRestMsg(GlobalNamespaceE.NAME),
                    action: this.NFSService.create(params, { doSuccess: true, mask: true, restNameParams: [GlobalNamespaceE.NAME] }),
                });

                return cmds;
            }

            const fileSystem: FileSystem = this.selectedFilesystem;
            let nfsParam: FileSystemResource;
            let cifsParam: FileSystemResource;

            // NFS
            if (vmValue.NFScreateShare && !this.shareResult?.NFS) {
                const vmNFS: NFSShare = vmValue.NFScreateShare[vmValue.NFScreateStyle].share;

                // 权限列表，如果是null，说明没有改动过permissions，只需要下发CIFS的ID就可以了。
                const vmNFSClient: NFSClient[] = vmValue.NFScreateShare[vmValue.NFScreateStyle].permissions;
                // 根文件系统不支持配置NFS权限
                nfsParam = this.collectionNFSShareParams(fileSystem.vstoreId, vmNFS,
                    (this.selectedFilesystem && this.selectedFilesystem.ID === GlobalNamespaceE.ID) ?
                        undefined : vmNFSClient);
            }
            // CIFS [根文件系统不支持cifs]
            if (vmValue.CIFScreateShare && !(this.selectedFilesystem && this.selectedFilesystem.ID === GlobalNamespaceE.ID)) {
                const vmCIFS: CIFSShare = vmValue.CIFScreateShare[vmValue.CIFScreateStyle].share;

                // 权限列表，如果是null，说明没有改动过permissions，只需要下发CIFS的ID就可以了。
                const vmCIFSUser: CIFSClient[] = vmValue.CIFScreateShare[vmValue.CIFScreateStyle].permissions;
                cifsParam = this.collectionCIFSShareParams(fileSystem.vstoreId, vmCIFS, vmCIFSUser);
            }

            cmds.push({
                name: this.str.createResult(fileSystem.NAME),
                action: this.service.createByOmtask(fileSystem.NAME, {
                    task_name: FileTaskNameE.createFileShare,
                    fileSystem,
                    ...nfsParam,
                    ...cifsParam,
                }),
            });

            return cmds;
        }
    }

    /**
     * 获取极简模式下：DM预置NAS定时计划下发参数
     * @returns
     */
    getSimpleNasDefaultScheduleParams(): HyperCdpSchedule {
        const defaultSchedule = SIMPLE_CDP_SCHEDULE_NAS_DEFAULT;

        const params: HyperCdpSchedule = {
            NAME: defaultSchedule.NAME,
            FREQUENCYVALUE: defaultSchedule.FREQUENCYVALUE,
            FREQUENCYNUM: defaultSchedule.FREQUENCYNUM,
            DAYHOURS: JSON.parse(defaultSchedule.DAYHOURS),
            DAYMINUTE: defaultSchedule.DAYMINUTE,
            DAILYSNAPSHOTNUM: defaultSchedule.DAILYSNAPSHOTNUM,
            WEEKLYDAYS: JSON.parse(defaultSchedule.WEEKLYDAYS),
            STARTTIMEOFWEEK: defaultSchedule.STARTTIMEOFWEEK,
            WEEKLYSNAPSHOTNUM: defaultSchedule.WEEKLYSNAPSHOTNUM,
            OBJECTTYPE: objectTypeE.file,
            vstoreId: SYSTEM_VSTORE_ID,
        };

        return params;
    }

    exec(): void {
        // 必须配置至少一个共享【1.未配置NFS共享场景：关闭NFS开关、选择的FS已存在NFS共享；2.未配置CIFS共享场景：关闭CIFS开关、跟文件系统隐藏CIFS共享】
        const cifsShareInVliad = !this.copyFromCifsShareS?.switch || this.hideCifsShare;
        if ((!this.copyFromNfsShareS?.switch || this.shareResult?.NFS) && cifsShareInVliad) {
            return;
        }
        // 开启CDP 并且是默认simple nas CDP策略时，DM先创建该策略
        if (this.vm.value.addToCdpScheduleSwitch && this.vm.value.selectedCdpSchedule && !this.vm.value.selectedCdpSchedule?.ID) {
            this.scheduleService.create(
                this.getSimpleNasDefaultScheduleParams(),
                SYSTEM_VSTORE_ID,
                {
                    restNameParams: [this.vm.value.selectedCdpSchedule.NAME],
                    doException: true,
                    mask: true,
                },
            ).pipe(
                switchMap((res: { data: HyperCdpSchedule }) => {
                    if (res.data?.ID) {
                        // 需立即启动新建的策略
                        return this.scheduleService.activate(
                            res.data.ID,
                            undefined,
                            {
                                restNameParams: [res.data.NAME],
                                doException: true,
                                mask: true,
                            },
                        );
                    } else {
                        return of(null);
                    }
                }),
            ).subscribe((response: HttpResponse) => {
                if (!response) {
                    return;
                }
                const { result: { code } } = response;
                if (code === ERROR_OK) {
                    super.exec();
                }
            });
        } else {
            super.exec();
        }
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.watchAssistant$?.unsubscribe();
    }
}
